/**
 * לוח מועדים — sold on the landing page, absent from the legacy.
 *
 * Two layers, and the second is the reason the screen exists: the timeline of what is due,
 * and the workload-per-week view derived from the estimated hours on each entry. A calendar
 * without the second layer is a list of dates, which the firm already has in its head.
 *
 * The weeks are bucketed server-side so the capacity view and any export agree.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { formatDate, formatMonthName } from "@onecpa/shared";
import { Badge, EmptyState, ErrorState, KpiStrip, PageHeader, Panel } from "@onecpa/ui";
import { api } from "../api/client.js";
import { useSession } from "../app/session.js";

const KIND_LABELS: Record<string, string> = {
  authority_report: "דיווח לרשות",
  annual_report: "דוח שנתי",
  wealth_declaration: "הצהרת הון",
  advance_payment: "מקדמות",
  custom: "אחר",
};

/** Above this many hours in one week, the firm is over capacity. */
const WEEKLY_CAPACITY_HOURS = 40;

export function DeadlineCalendar() {
  const session = useSession();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["calendar", session.orgId],
    queryFn: () => api.analytics.calendar(session.orgId),
  });

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  const entries = data?.entries ?? [];
  const weeks = data?.workloadByWeek ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = entries.filter((entry) => entry.dueDate >= today && !entry.completedAt);
  const overdue = entries.filter((entry) => entry.dueDate < today && !entry.completedAt);
  const totalHours = entries.reduce((sum, entry) => sum + (entry.estimatedHours ?? 0), 0);
  const peakWeek = weeks.reduce<{ week: string; hours: number } | null>(
    (peak, week) => (!peak || week.hours > peak.hours ? week : peak),
    null,
  );

  const byMonth = new Map<string, typeof entries>();
  for (const entry of entries) {
    const key = entry.dueDate.slice(0, 7);
    const list = byMonth.get(key) ?? [];
    list.push(entry);
    byMonth.set(key, list);
  }

  return (
    <>
      <PageHeader
        title="לוח מועדים"
        subtitle={
          data ? `${formatDate(data.range.from)} – ${formatDate(data.range.to)}` : undefined
        }
      />

      <KpiStrip
        loading={isLoading}
        items={[
          { label: "מועדים בטווח", value: entries.length },
          { label: "לפנינו", value: upcoming.length },
          { label: "חלפו", value: overdue.length, tone: overdue.length > 0 ? "ochre" : "ink" },
          { label: "שעות מוערכות", value: totalHours },
          {
            label: "שבוע עמוס",
            value: peakWeek ? `${peakWeek.hours} ש׳` : "—",
            tone: (peakWeek?.hours ?? 0) > WEEKLY_CAPACITY_HOURS ? "red" : "ink",
          },
        ]}
      />

      <div className="grid gap-4 p-5 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          {isLoading && (
            <Panel>
              <div className="px-3.5 py-6 text-xs text-ink-faint">טוען…</div>
            </Panel>
          )}

          {[...byMonth.entries()].map(([month, monthEntries]) => (
            <Panel
              key={month}
              title={`${formatMonthName(Number(month.slice(5, 7)))} ${month.slice(0, 4)}`}
            >
              <ul className="divide-y divide-line-soft">
                {monthEntries.map((entry) => (
                  <li key={entry.id} className="flex flex-wrap items-center gap-3 px-3.5 py-2.5">
                    <bdi className="tnum w-20 text-xs font-semibold text-ink">
                      {formatDate(entry.dueDate)}
                    </bdi>
                    <span className="min-w-40 flex-1">
                      {entry.clientId ? (
                        <Link
                          to="/clients/$clientId"
                          params={{ clientId: entry.clientId }}
                          className="link-underline block truncate text-xs text-ink"
                        >
                          {entry.title}
                        </Link>
                      ) : (
                        <span className="block truncate text-xs text-ink">{entry.title}</span>
                      )}
                      {entry.assignedStaffName && (
                        <span className="block text-2xs text-ink-faint">
                          {entry.assignedStaffName}
                        </span>
                      )}
                    </span>
                    <Badge tone="neutral">{KIND_LABELS[entry.kind] ?? entry.kind}</Badge>
                    {entry.estimatedHours ? (
                      <span className="tnum text-2xs text-ink-faint">
                        {entry.estimatedHours} ש׳
                      </span>
                    ) : null}
                    {entry.completedAt ? (
                      <Badge tone="success">הושלם</Badge>
                    ) : entry.dueDate < today ? (
                      <Badge tone="warning">חלף</Badge>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Panel>
          ))}

          {!isLoading && entries.length === 0 && (
            <Panel>
              <EmptyState
                title="אין מועדים בטווח"
                description="מועדים נוצרים מתדירויות הדיווח של הלקוחות ומתיקים שנפתחו."
                icon="calendar-check"
              />
            </Panel>
          )}
        </div>

        <Panel title="עומס לפי שבוע">
          {weeks.length > 0 ? (
            <>
              <ul className="divide-y divide-line-soft">
                {weeks.map((week) => {
                  const over = week.hours > WEEKLY_CAPACITY_HOURS;
                  return (
                    <li key={week.week} className="flex items-center gap-2 px-3.5 py-2">
                      <bdi className="tnum w-20 text-2xs text-ink-muted">
                        {formatDate(week.week)}
                      </bdi>
                      <span
                        className="h-1.5 flex-1 overflow-hidden rounded-[2px] bg-wash"
                        dir="ltr"
                      >
                        <span
                          className={`block h-full ${over ? "bg-red-ink" : "bg-ledger"}`}
                          style={{
                            width: `${Math.min(100, (week.hours / WEEKLY_CAPACITY_HOURS) * 100).toFixed(0)}%`,
                          }}
                        />
                      </span>
                      <bdi
                        className={`tnum w-12 text-end text-2xs ${over ? "font-semibold text-red-ink" : "text-ink"}`}
                      >
                        {week.hours} ש׳
                      </bdi>
                    </li>
                  );
                })}
              </ul>
              <p className="border-t border-line-soft px-3.5 py-2 text-2xs text-ink-faint">
                הפס מתמלא ביחס ל-{WEEKLY_CAPACITY_HOURS} שעות שבועיות · אדום = מעל הקיבולת
              </p>
            </>
          ) : (
            <EmptyState title="אין נתוני עומס" />
          )}
        </Panel>
      </div>
    </>
  );
}
